import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils';
import { TovButton, type TovButtonProps } from '../TovButton/TovButton';
import { useDialogPortalContainer } from '../TovDialogPortalContext/TovDialogPortalContext';

type TovMenuContextValue = {
  setOpen(open: boolean): void;
};

const TovMenuContext = React.createContext<TovMenuContextValue | null>(null);
const MENU_MOBILE_QUERY = '(max-width: 720px)';
const MENU_VIEWPORT_GUTTER = 12;
const MENU_TRIGGER_OFFSET = 7;

function useIsMobileMenu() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MENU_MOBILE_QUERY);
    const syncState = () => setIsMobile(mediaQuery.matches);

    syncState();

    mediaQuery.addEventListener('change', syncState);
    return () => mediaQuery.removeEventListener('change', syncState);
  }, []);

  return isMobile;
}

export interface TovMenuProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  defaultOpen?: boolean;
  onOpenChange?(open: boolean): void;
  open?: boolean;
  portal?: boolean;
  renderTrigger(triggerProps: {
    'aria-expanded': boolean;
    'aria-haspopup': 'menu';
    onClick: () => void;
  }): React.ReactElement;
}

export function TovMenu({
  children,
  className,
  contentClassName,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
  portal = false,
  renderTrigger,
}: TovMenuProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const [portalPanelStyle, setPortalPanelStyle] =
    React.useState<React.CSSProperties>();
  const portalContainer = useDialogPortalContainer();
  const isMobileMenu = useIsMobileMenu();
  const open = controlledOpen ?? uncontrolledOpen;
  const mobilePanelStyle = isMobileMenu
    ? ({
        bottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
        left: 'max(0.75rem, env(safe-area-inset-left, 0px))',
        maxHeight: 'min(80dvh, 34rem)',
        maxWidth: 'none',
        overflowY: 'auto',
        position: 'fixed',
        right: 'max(0.75rem, env(safe-area-inset-right, 0px))',
        top: 'auto',
        width: 'auto',
      } satisfies React.CSSProperties)
    : undefined;

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) {
        setUncontrolledOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange],
  );

  const updatePortalPanelStyle = React.useCallback(() => {
    if (!portal || isMobileMenu || typeof window === 'undefined') {
      setPortalPanelStyle(undefined);
      return;
    }

    const triggerRect = containerRef.current?.getBoundingClientRect();

    if (!triggerRect) {
      setPortalPanelStyle(undefined);
      return;
    }

    const availableBelow =
      window.innerHeight - triggerRect.bottom - MENU_VIEWPORT_GUTTER;
    const availableAbove = triggerRect.top - MENU_VIEWPORT_GUTTER;
    const shouldOpenAbove =
      availableBelow < 160 && availableAbove > availableBelow;
    const maxHeight = Math.max(
      96,
      Math.floor(shouldOpenAbove ? availableAbove : availableBelow),
    );

    setPortalPanelStyle({
      ...(shouldOpenAbove
        ? {
            bottom: window.innerHeight - triggerRect.top + MENU_TRIGGER_OFFSET,
          }
        : {
            top: triggerRect.bottom + MENU_TRIGGER_OFFSET,
          }),
      maxHeight,
      position: 'fixed',
      right: Math.max(
        MENU_VIEWPORT_GUTTER,
        window.innerWidth - triggerRect.right,
      ),
    });
  }, [isMobileMenu, portal]);

  React.useLayoutEffect(() => {
    if (!open || !portal) {
      return undefined;
    }

    updatePortalPanelStyle();
    window.addEventListener('resize', updatePortalPanelStyle);
    window.addEventListener('scroll', updatePortalPanelStyle, true);

    return () => {
      window.removeEventListener('resize', updatePortalPanelStyle);
      window.removeEventListener('scroll', updatePortalPanelStyle, true);
    };
  }, [open, portal, updatePortalPanelStyle]);

  React.useEffect(() => {
    function onDocumentMouseDown(event: MouseEvent) {
      const target = event.target as Node;

      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }

    function onDocumentKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    if (!open) {
      return undefined;
    }

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [open, setOpen]);

  const renderedMenu = open ? (
    <>
      {isMobileMenu ? (
        <div
          aria-hidden="true"
          className="tov-topbar-menu__mobile-backdrop"
          onClick={() => setOpen(false)}
        />
      ) : null}
      <div
        className={cn('tov-topbar-menu__panel', contentClassName)}
        data-mobile={isMobileMenu ? 'true' : undefined}
        ref={panelRef}
        role="menu"
        style={isMobileMenu ? mobilePanelStyle : portalPanelStyle}
      >
        {children}
      </div>
    </>
  ) : null;

  return (
    <TovMenuContext.Provider value={{ setOpen }}>
      <div className={cn('tov-topbar-menu', className)} ref={containerRef}>
        {renderTrigger({
          'aria-expanded': open,
          'aria-haspopup': 'menu',
          onClick: () => setOpen(!open),
        })}
        {portal && renderedMenu && typeof document !== 'undefined'
          ? createPortal(renderedMenu, portalContainer ?? document.body)
          : renderedMenu}
      </div>
    </TovMenuContext.Provider>
  );
}

export function TovMenuList({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return <div className={cn('tov-topbar-menu__list', className)} {...props} />;
}

export interface TovMenuItemProps extends TovButtonProps {
  active?: boolean;
  closeOnSelect?: boolean;
}

export function TovMenuItem({
  active = false,
  children,
  className,
  closeOnSelect = true,
  onClick,
  variant = 'ghost',
  ...props
}: TovMenuItemProps) {
  const menuContext = React.useContext(TovMenuContext);

  return (
    <TovButton
      className={cn(
        'tov-topbar-menu__item',
        active && 'tov-topbar-menu__item--active',
        className,
      )}
      data-active={active ? 'true' : undefined}
      onClick={(event) => {
        onClick?.(event);

        if (!event.defaultPrevented && closeOnSelect) {
          menuContext?.setOpen(false);
        }
      }}
      role="menuitem"
      variant={variant}
      {...props}
    >
      {children}
    </TovButton>
  );
}

export function TovMenuItemMedia({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span className={cn('tov-topbar-menu__item-media', className)} {...props} />
  );
}

export function TovMenuItemCopy({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span className={cn('tov-topbar-menu__item-copy', className)} {...props} />
  );
}

export function TovMenuItemTitle({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span className={cn('tov-topbar-menu__item-title', className)} {...props} />
  );
}

export function TovMenuItemMeta({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span className={cn('tov-topbar-menu__item-meta', className)} {...props} />
  );
}

export function TovMenuItemIndicator({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn('tov-topbar-menu__item-indicator', className)}
      {...props}
    />
  );
}

export function TovMenuSeparator({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      aria-hidden="true"
      className={cn('tov-topbar-menu__separator', className)}
      {...props}
    />
  );
}

export type TovTopbarMenuProps = TovMenuProps;
export type TovTopbarMenuItemProps = TovMenuItemProps;

export const TovTopbarMenu = TovMenu;
export const TovTopbarMenuList = TovMenuList;
export const TovTopbarMenuItem = TovMenuItem;
export const TovTopbarMenuItemMedia = TovMenuItemMedia;
export const TovTopbarMenuItemCopy = TovMenuItemCopy;
export const TovTopbarMenuItemTitle = TovMenuItemTitle;
export const TovTopbarMenuItemMeta = TovMenuItemMeta;
export const TovTopbarMenuItemIndicator = TovMenuItemIndicator;
export const TovTopbarMenuSeparator = TovMenuSeparator;
